// ============================================================
// RegisterScreen.tsx
// INVITE-ONLY notice (24 June onwards).
//
// Casevo switched to invite-only accounts on 24 June; the
// backend permanently 403s POST /api/auth/register (see
// backend/src/controllers/authController.ts:19-22). The form
// this screen used to host would hit that dead endpoint.
//
// The component + its `authView === 'register'` case in App.tsx
// are DELIBERATELY LEFT REACHABLE so stale bookmarks and any
// pre-24-June invitation-style emails that pointed here still
// resolve to something coherent — the notice below, plus the
// "Already have an account? Sign in" link back to LoginScreen.
//
// The old form, submit handler, validation, error banner,
// spinner and the registerApi import were all removed — nothing
// on this screen can talk to the server any more. Styling of
// the notice matches the pre-existing page shell verbatim (nav
// header, casevo-offwhite bg, brand-coloured micro-label,
// forest H1, mid-body paragraph, brand-underline sign-in link).
// ============================================================
import React from 'react'

interface RegisterScreenProps {
  onShowLogin: () => void
}

const RegisterScreen: React.FC<RegisterScreenProps> = ({ onShowLogin }) => {
  return (
    <div className="min-h-screen bg-casevo-offwhite font-sans flex flex-col">
      <nav className="bg-casevo-header px-6 h-14 flex items-center border-b border-casevo-border">
        <div className="flex items-center gap-3">
          <svg width="28" height="28" viewBox="0 0 68 68">
            <circle cx="34" cy="34" r="34" fill="#2d6a4f"/>
            <rect x="23" y="17" width="9" height="34" rx="4.5" fill="#ffffff"/>
            <rect x="36" y="22" width="9" height="24" rx="4.5" fill="#74c69d"/>
          </svg>
          <div className="flex flex-col">
            <span className="text-white font-medium text-sm tracking-widest uppercase leading-none">
              Casevo
            </span>
            <span className="text-casevo-light text-xs tracking-widest uppercase leading-none">
              Case Management
            </span>
          </div>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm text-center">
          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest text-casevo-brand mb-2">
              Invite only
            </p>
            <h1 className="text-2xl font-medium text-casevo-forest">
              Casevo is invite-only
            </h1>
          </div>
          <p className="text-sm text-casevo-mid mb-6">
            Accounts are created by invitation. Ask your firm administrator to send you an invite.
          </p>
          <p className="text-center text-sm text-casevo-mid">
            Already have an account?{' '}
            <button
              onClick={onShowLogin}
              className="text-casevo-brand underline"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
export default RegisterScreen
